import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  BedDouble, Loader2, Sparkles, Save, Wand2, Euro, TrendingUp, Users, Droplets, Flame,
  BarChart3, Upload, ArrowRight, Info, X, RotateCcw,
} from "lucide-react";

export default function Rooms() {
  const { lang, properties, activeId } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const active = properties.find((p) => p.id === activeId) || properties[0];

  const [cfg, setCfg] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxLabels, setTaxLabels] = useState({});
  const [amenities, setAmenities] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    if (!active?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${active.id}/rooms?lang=${lang}`);
      setCfg(data.rooms_config || null);
      setExcluded(data.excluded_room_names || []);
      setTaxonomy(data.taxonomy || []);
      setTaxLabels(data.taxonomy_labels || {});
      setAmenities(data.amenities || []);
      if (data.rooms_config?.rooms?.length) {
        const [pr, bn] = await Promise.all([
          api.get(`/properties/${active.id}/room-pricing?lang=${lang}`),
          api.get(`/properties/${active.id}/room-benchmark?lang=${lang}`),
        ]);
        setPricing(pr.data); setBench(bn.data);
      } else { setPricing(null); setBench(null); }
    } catch { setCfg(null); } finally { setLoading(false); }
  }, [active?.id, lang]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const detect = async () => {
    if (!window.confirm(L(
      "Rianalizzo le camere dall'ultimo file importato. Le camere che hai escluso NON verranno riaggiunte. Continuo?",
      "Re-analyze rooms from the last imported file. Rooms you excluded will NOT be re-added. Continue?"))) return;
    setDetecting(true);
    try {
      const { data } = await api.post(`/properties/${active.id}/rooms/detect?lang=${lang}`);
      setCfg(data.rooms_config);
      toast.success(L("Camere rianalizzate dal file", "Rooms re-analyzed from file"));
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
    finally { setDetecting(false); }
  };

  const excludeRoom = async (name) => {
    if (!window.confirm(L(
      `Escludo la camera "${name}" da tutti i calcoli (occupazione, medie, prezzi). Potrai ripristinarla dalla sezione "Camere escluse". Continuo?`,
      `Exclude room "${name}" from all calculations (occupancy, averages, prices). You can restore it from the "Excluded rooms" section. Continue?`))) return;
    try {
      const { data } = await api.post(`/properties/${active.id}/rooms/exclude`, { name });
      setCfg(data.rooms_config); setExcluded(data.excluded_room_names || []);
      toast.success(L(`Camera "${name}" esclusa dai calcoli`, `Room "${name}" excluded from calculations`));
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
  };

  const includeRoom = async (name) => {
    try {
      const { data } = await api.post(`/properties/${active.id}/rooms/include`, { name });
      setCfg(data.rooms_config); setExcluded(data.excluded_room_names || []);
      toast.success(L(`Camera "${name}" ripristinata`, `Room "${name}" restored`));
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
  };

  const updateRoom = (i, patch) => {
    setCfg((c) => ({ ...c, rooms: c.rooms.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  };
  const updateAmenity = (i, key, val) => {
    setCfg((c) => ({ ...c, rooms: c.rooms.map((r, idx) => idx === i ? { ...r, amenities: { ...(r.amenities || {}), [key]: val } } : r) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${active.id}/rooms`, { rooms: cfg.rooms });
      toast.success(L("Camere salvate", "Rooms saved"));
      await loadAll();
    } catch { toast.error(L("Errore nel salvataggio", "Save error")); }
    finally { setSaving(false); }
  };

  const AMENITY_ICON = { jacuzzi: Droplets, hammam: Flame };
  const AMENITY_LABEL = { jacuzzi: "Jacuzzi", hammam: "Hammam" };

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="rooms-page">
        <div className="flex items-center gap-3 mb-6">
          <BedDouble className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{L("Camere", "Rooms")}</h1>
            <p className="text-sm text-muted-foreground">{active ? `${active.name} · ${active.city}` : L("Seleziona una struttura", "Select a property")}</p>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />{L("Carico le camere…", "Loading rooms…")}</div>
        ) : !cfg?.rooms?.length ? (
          <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center" data-testid="rooms-empty">
            <Sparkles className="w-7 h-7 text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-1 max-w-xl mx-auto">{L("Carica il file delle prenotazioni: l'AI rileverà automaticamente le tipologie delle tue camere e potrai poi rivederle a mano.", "Upload your bookings file: the AI will auto-detect your room types and you can then review them manually.")}</p>
            <button data-testid="rooms-import-btn" onClick={() => nav("/import")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
              <Upload className="w-4 h-4" />{L("Importa prenotazioni", "Import reservations")}<ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <Tabs defaultValue="config" data-testid="rooms-tabs">
            <TabsList className="mb-4">
              <TabsTrigger value="config" data-testid="rooms-tab-config">{L("Configurazione", "Configuration")}</TabsTrigger>
              <TabsTrigger value="pricing" data-testid="rooms-tab-pricing">{L("Prezzi AI", "AI Pricing")}</TabsTrigger>
            </TabsList>

            <TabsContent value="config">
            {/* Room manager */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="rooms-manager">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Gestione camere", "Room management")}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{L(`${cfg.rooms.length} camere · tipologia, ospiti e servizi`, `${cfg.rooms.length} rooms · type, guests and amenities`)}</p>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="rooms-detect-btn" variant="outline" size="sm" onClick={detect} disabled={detecting}>
                    {detecting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1.5" />}{L("Rianalizza dal file", "Re-analyze from file")}
                  </Button>
                  <Button data-testid="rooms-save-btn" size="sm" onClick={save} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}{L("Salva", "Save")}
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="px-2 py-2">{L("Camera", "Room")}</th>
                      <th className="px-2 py-2">{L("Tipologia", "Type")}</th>
                      <th className="px-2 py-2 text-center">{L("Max ospiti", "Max guests")}</th>
                      <th className="px-2 py-2 text-center">Jacuzzi</th>
                      <th className="px-2 py-2 text-center">Hammam</th>
                      <th className="px-2 py-2 text-center">{L("Azioni", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cfg.rooms.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-border" data-testid={`room-row-${i}`}>
                        <td className="px-2 py-2">
                          <Input data-testid={`room-name-${i}`} value={r.name} onChange={(e) => updateRoom(i, { name: e.target.value })} className="h-9 max-w-[220px]" />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={r.type} onValueChange={(v) => updateRoom(i, { type: v })}>
                            <SelectTrigger data-testid={`room-type-${i}`} className="h-9 w-[170px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {taxonomy.map((t) => <SelectItem key={t} value={t}>{taxLabels[t] || t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <Input data-testid={`room-guests-${i}`} type="number" min={1} max={12} value={r.max_guests}
                            onChange={(e) => updateRoom(i, { max_guests: Number(e.target.value) })} className="h-9 w-[70px] mx-auto text-center font-data" />
                        </td>
                        {amenities.map((a) => (
                          <td key={a} className="px-2 py-2 text-center">
                            <Switch data-testid={`room-${a}-${i}`} checked={!!(r.amenities || {})[a]} onCheckedChange={(v) => updateAmenity(i, a, v)} />
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center">
                          <button data-testid={`room-delete-${i}`} onClick={() => excludeRoom(r.name)}
                            title={L("Elimina camera (escludi dai calcoli)", "Delete room (exclude from calculations)")}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {excluded.length > 0 && (
                <div className="mt-6 border-t border-border pt-4" data-testid="rooms-excluded">
                  <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1">{L("Camere escluse dai calcoli", "Rooms excluded from calculations")}</h3>
                  <p className="text-[11px] text-muted-foreground mb-3">{L("Queste camere non vengono conteggiate in occupazione, medie, prezzi e benchmark. Ripristinale se le hai eliminate per sbaglio.", "These rooms are not counted in occupancy, averages, prices and benchmark. Restore them if you removed one by mistake.")}</p>
                  <div className="flex flex-wrap gap-2">
                    {excluded.map((nm, i) => (
                      <div key={nm + i} data-testid={`excluded-room-${i}`} className="inline-flex items-center gap-2 rounded-full bg-muted/60 border border-border pl-3 pr-1 py-1 text-sm">
                        <span className="text-muted-foreground line-through">{nm}</span>
                        <button data-testid={`room-restore-${i}`} onClick={() => includeRoom(nm)}
                          title={L("Ripristina camera", "Restore room")}
                          className="inline-flex items-center gap-1 rounded-full bg-background border border-border px-2 py-0.5 text-[11px] font-medium hover:text-primary hover:border-primary transition-colors">
                          <RotateCcw className="w-3 h-3" />{L("Ripristina", "Restore")}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-6">
            {!(pricing?.types?.length > 0) && !(bench?.own?.types && Object.keys(bench.own.types).length > 0) && (
              <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground" data-testid="pricing-empty">
                {L("Il Room Pricing Engine si attiva quando c'è abbastanza storico importato per apprendere i differenziali per tipologia. Importa il tuo storico prenotazioni per vedere prezzi, ADR e occupazione per ogni tipologia.", "The Room Pricing Engine activates once there is enough imported history to learn per-type differentials. Import your booking history to see prices, ADR and occupancy per room type.")}
              </div>
            )}

            {/* Room Pricing Engine */}
            {pricing?.types?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border-2 border-primary/25 rounded-lg p-6" data-testid="room-pricing">
                <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">Room Pricing Engine</h2></div>
                <p className="text-sm text-muted-foreground mb-4">{L("Il prezzo di mercato viene declinato su ogni tipologia usando i differenziali appresi dal tuo storico.", "The market price is broken down per room type using the differentials learned from your history.")}</p>
                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5"><Euro className="w-4 h-4 text-muted-foreground" />{L("Prezzo di mercato", "Market price")}: <b className="font-data">€{pricing.market_price}</b></span>
                  <span className="text-muted-foreground">{L("Camera base", "Base room")}: <b className="text-foreground">{pricing.base_label}</b></span>
                  {!pricing.learned && <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Info className="w-3.5 h-3.5" />{L("differenziali di default (poco storico)", "default differentials (little history)")}</span>}
                </div>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm" data-testid="room-pricing-table">
                    <thead>
                      <tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="text-left px-3 py-2">{L("Tipologia", "Type")}</th>
                        <th className="text-right px-3 py-2" title={L("Prezzo consigliato pubblicato", "Recommended published price")}>{L("Consigliato Lordo", "Recommended Gross")}</th>
                        <th className="text-right px-3 py-2" title={L("Prezzo consigliato netto (tua calibrazione)", "Recommended net price (your calibration)")}>{L("Consigliato Netto", "Recommended Net")}</th>
                        <th className="text-right px-3 py-2">{L("Differenziale", "Differential")}</th>
                        <th className="text-right px-3 py-2" title={L("Prezzo netto a cui hai venduto questa camera per notte (dallo storico)", "Net price you actually sold this room per night (from history)")}>{L("Venduto storico (netto)", "Sold history (net)")}</th>
                        <th className="text-right px-3 py-2">{L("Occupazione", "Occupancy")}</th>
                        <th className="text-right px-3 py-2">{L("Vende fino a", "Sells up to")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricing.types.map((t, i) => (
                        <tr key={t.type} className={`border-t border-border ${t.is_base ? "bg-primary/5" : ""}`} data-testid={`room-price-${t.type}`}>
                          <td className="px-3 py-2 font-medium">{t.label}{t.is_base && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground uppercase">base</span>}<span className="ml-2 text-[11px] text-muted-foreground">×{t.rooms}</span></td>
                          <td className="px-3 py-2 text-right font-data font-semibold text-base">€{t.price}{t.capped && <span className="ml-1 text-[10px] text-amber-600">cap</span>}</td>
                          <td className="px-3 py-2 text-right font-data font-semibold text-base text-primary" data-testid={`room-price-net-${t.type}`}>{t.price_net != null ? `€${t.price_net}` : `€${t.price}`}</td>
                          <td className="px-3 py-2 text-right font-data" style={{ color: t.diff_pct > 0 ? "#16a34a" : t.diff_pct < 0 ? "#ef4444" : undefined }}>{t.diff_pct > 0 ? "+" : ""}{t.diff_pct}%</td>
                          <td className="px-3 py-2 text-right font-data text-muted-foreground">{t.adr_history != null ? `€${t.adr_history}` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data text-muted-foreground">{t.occupancy != null ? `${t.occupancy}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data text-muted-foreground">{t.sells_up_to != null ? `€${t.sells_up_to}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pricing.rooms?.length > 0 && (
                  <div className="mt-5">
                    <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">{L("Prezzo per singola camera", "Price per individual room")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {pricing.rooms.map((r, i) => (
                        <div key={r.id || i} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2.5" data-testid={`room-price-card-${i}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">{r.label} · <Users className="w-3 h-3" />{r.max_guests}
                              {Object.entries(r.amenities || {}).filter(([, v]) => v).map(([a]) => { const Ic = AMENITY_ICON[a]; return <span key={a} className="inline-flex items-center gap-0.5">{Ic && <Ic className="w-3 h-3" />}{AMENITY_LABEL[a]}</span>; })}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-data font-semibold" data-testid={`room-card-net-${i}`}>{L("Netto", "Net")} €{r.price_net != null ? r.price_net : r.price}</p>
                            <p className="text-[11px] text-muted-foreground">{L("Lordo", "Gross")} €{r.price}</p>
                            {r.amenity_uplift_pct > 0 && <p className="text-[10px] text-emerald-600">+{r.amenity_uplift_pct}%</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Room Benchmark Engine */}
            {bench?.own?.types && Object.keys(bench.own.types).length > 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="room-benchmark">
                <div className="flex items-center gap-2 mb-1"><BarChart3 className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">Room Benchmark</h2></div>
                <p className="text-sm text-muted-foreground mb-4">{L("Metriche per ogni tipologia dal tuo storico. Il benchmark anonimo di strutture simili si attiva con almeno 5 strutture comparabili.", "Metrics per room type from your history. The anonymous benchmark of similar structures unlocks with at least 5 comparable structures.")}</p>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm" data-testid="room-benchmark-table">
                    <thead>
                      <tr className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="text-left px-3 py-2">{L("Tipologia", "Type")}</th>
                        <th className="text-right px-3 py-2">ADR</th>
                        <th className="text-right px-3 py-2">{L("Occup.", "Occ.")}</th>
                        <th className="text-right px-3 py-2">Lead time</th>
                        <th className="text-right px-3 py-2">Pickup</th>
                        <th className="text-right px-3 py-2">{L("Elasticità", "Elasticity")}</th>
                        <th className="text-right px-3 py-2">{L("Sconto", "Discount")}</th>
                        <th className="text-right px-3 py-2">{L("Conversione", "Conversion")}</th>
                        <th className="text-right px-3 py-2">{L("Diff. vs base", "Diff vs base")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(bench.own.types).map(([t, s]) => (
                        <tr key={t} className="border-t border-border" data-testid={`room-bench-${t}`}>
                          <td className="px-3 py-2 font-medium">{bench.labels?.[t] || t}</td>
                          <td className="px-3 py-2 text-right font-data">{s.adr != null ? `€${s.adr}` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.occupancy != null ? `${s.occupancy}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.lead_time != null ? L(`${s.lead_time}gg`, `${s.lead_time}d`) : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.pickup != null ? s.pickup : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.elasticity != null ? `${s.elasticity}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.discount_rate != null ? `${s.discount_rate}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data">{s.conversion != null ? `${s.conversion}%` : "—"}</td>
                          <td className="px-3 py-2 text-right font-data" style={{ color: s.diff_pct > 0 ? "#16a34a" : s.diff_pct < 0 ? "#ef4444" : undefined }}>{s.diff_pct != null ? `${s.diff_pct > 0 ? "+" : ""}${s.diff_pct}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="room-bench-anon">
                  <Info className="w-3.5 h-3.5" />
                  {(() => {
                    const s = bench.anonymous?.structures || 0;
                    const strut = s === 1 ? L("struttura simile", "similar structure") : L("strutture simili", "similar structures");
                    return bench.anonymous?.available
                      ? L(`Benchmark anonimo attivo su ${s} ${strut}. L'affidabilità aumenterà automaticamente con l'ingresso di nuove strutture.`, `Anonymous benchmark active on ${s} ${strut}. Reliability will keep increasing automatically as new structures join.`)
                      : L(`Benchmark in crescita: ${s} ${strut} finora. L'affidabilità aumenterà automaticamente con l'ingresso di nuove strutture.`, `Benchmark growing: ${s} ${strut} so far. Reliability will increase automatically as new structures join.`);
                  })()}
                </div>
              </motion.div>
            )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
